import {
  $convertFromMarkdownString,
  CODE,
  type ElementTransformer,
  HEADING as HEADING_ORIG,
  type MultilineElementTransformer,
  ORDERED_LIST as ORDERED_LIST_ORIG,
  QUOTE as QUOTE_ORIG,
  type TextFormatTransformer,
  type TextMatchTransformer,
  type Transformer,
  UNORDERED_LIST as UNORDERED_LIST_ORIG,
} from "@lexical/markdown";
import { $createQuoteNode, $isQuoteNode, QuoteNode } from "@lexical/rich-text";
import {
  $createTableNodeWithDimensions,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import {
  $createLineBreakNode,
  $createTabNode,
  $createTextNode,
  $isElementNode,
  $isLineBreakNode,
  $isRootNode,
  $isTabNode,
  type ElementNode,
  type LexicalNode,
} from "lexical";

import { BACKGROUND_ALPHA, DEFAULT_HIGHLIGHT_COLOR } from "~/utils/color";

type HighlightCallback = (color: string, isBackground: boolean) => void;
type CreateImageAttachmentCallback = (url: string, alt: string) => LexicalNode;
type CreateEntityCallback = (propertyDuid: string, value: string) => LexicalNode;
type CreateDartLinkCallback = (link: string, kind: string, duid: string | undefined, label: string) => LexicalNode;

let onHighlightColor: HighlightCallback | null = null;
let createImageAttachment: CreateImageAttachmentCallback | null = null;
let createEntity: CreateEntityCallback | null = null;
let createDartLink: CreateDartLinkCallback | null = null;
let cellTransformers: Transformer[] | undefined;

export const initMarkdown = (
  highlightCallback: HighlightCallback,
  createImageAttachmentCallback: CreateImageAttachmentCallback,
  createEntityCallback: CreateEntityCallback,
  createDartLinkCallback: CreateDartLinkCallback,
  transformers: Transformer[]
) => {
  onHighlightColor = highlightCallback;
  createImageAttachment = createImageAttachmentCallback;
  createEntity = createEntityCallback;
  createDartLink = createDartLinkCallback;
  cellTransformers = transformers.filter(({ type }) => type === "text-format" || type === "text-match");
};

// Inspired by @lexical/markdown/LexicalMarkdown.dev.js
export const createBlockNode =
  (createNode: (match: string[]) => LexicalNode) =>
  (parentNode: ElementNode, children: LexicalNode[] | null, match: string[]) => {
    const node = createNode(match);
    if ($isElementNode(node) && children) {
      node.append(...children);
    }
    if ($isRootNode(parentNode)) {
      parentNode.append(node);
    } else {
      parentNode.replace(node);
    }

    if ($isElementNode(node)) {
      node.select(0, 0);
    }
  };

// These are the generic lexical transformers, for custom node transformers they should go in the custom node's file
export const CODE_BLOCK: MultilineElementTransformer = CODE;

export const HEADING: ElementTransformer = {
  ...HEADING_ORIG,
  regExp: /^(#{1,3})\s/,
};

export const QUOTE: ElementTransformer = {
  ...QUOTE_ORIG,
  dependencies: [QuoteNode],
  regExp: /^(>+)\s/,
  replace: (parentNode, children, match) => {
    const count = match[1].length;

    let current = $createQuoteNode();

    if ($isQuoteNode(parentNode)) {
      // Keep the parent quote and add new levels inside it
      parentNode.clear();
      parentNode.append(current);
    } else if ($isRootNode(parentNode)) {
      parentNode.append(current);
    } else {
      parentNode.replace(current);
    }

    for (let i = 1; i < count; i += 1) {
      const nested = $createQuoteNode();
      current.append(nested);
      current = nested;
    }

    current.append(...children);
    current.select(0, 0);
  },
  type: "element",
};

export const ORDERED_LIST = {
  ...ORDERED_LIST_ORIG,
  regExp: /^(\s*)(\d+)(?:[.)/]{1,2}| -)\s/,
};

export const UNORDERED_LIST = {
  ...UNORDERED_LIST_ORIG,
  regExp: /^(\s*)[-*+•◦‣⁃]\s/,
};

export const ITALICS_STAR: TextFormatTransformer = {
  format: ["italic"],
  tag: "*",
  intraword: false,
  type: "text-format",
};

export const ITALICS_UNDERSCORE: TextFormatTransformer = {
  format: ["italic"],
  tag: "_",
  intraword: false,
  type: "text-format",
};

export const BOLD_STAR_DOUBLE: TextFormatTransformer = {
  format: ["bold"],
  tag: "**",
  intraword: false,
  type: "text-format",
};

export const BOLD_UNDERSCORE_DOUBLE: TextFormatTransformer = {
  format: ["bold"],
  tag: "__",
  intraword: false,
  type: "text-format",
};

export const BOLD_ITALIC_STAR_TRIPLE: TextFormatTransformer = {
  format: ["bold", "italic"],
  tag: "***",
  intraword: false,
  type: "text-format",
};

export const BOLD_ITALIC_UNDERSCORE_TRIPLE: TextFormatTransformer = {
  format: ["bold", "italic"],
  tag: "___",
  intraword: false,
  type: "text-format",
};

export const STRIKE_THROUGH_TILDE: TextFormatTransformer = {
  format: ["strikethrough"],
  tag: "~~",
  intraword: false,
  type: "text-format",
};

export const CODE_BACKTICK: TextFormatTransformer = {
  format: ["code"],
  tag: "`",
  intraword: false,
  type: "text-format",
};

const UNDERLINE_REGEXP = /<(u|ins)>([^<>]+)<\/\1>/;
export const UNDERLINE: TextMatchTransformer = {
  dependencies: [],
  regExp: UNDERLINE_REGEXP,
  importRegExp: UNDERLINE_REGEXP,
  replace: (textNode, match) => {
    textNode.setTextContent(match[2]);
    textNode.setFormat("underline");
    return textNode;
  },
  trigger: ">",
  type: "text-match",
};

const HIGHLIGHT_REGEXP = /(?<=^|\s)==([^=\s](?:[^=]*[^=\s])?)==(?=\s|$)/;
export const HIGHLIGHT_EQUALS: TextMatchTransformer = {
  dependencies: [],
  regExp: HIGHLIGHT_REGEXP,
  importRegExp: HIGHLIGHT_REGEXP,
  replace: (textNode, match) => {
    textNode.setTextContent(match[1]);
    textNode.setStyle(`background-color: ${DEFAULT_HIGHLIGHT_COLOR}${BACKGROUND_ALPHA}`);
    onHighlightColor?.(DEFAULT_HIGHLIGHT_COLOR, true);
    const afterNode = $createTextNode();
    textNode.insertAfter(afterNode);
    afterNode.select();
    return textNode;
  },
  trigger: "=",
  type: "text-match",
};

const IMAGE_REGEXP = /!\[(.*?)\]\(([^)]+)\)/;
export const IMAGE: TextMatchTransformer = {
  dependencies: [],
  regExp: IMAGE_REGEXP,
  importRegExp: IMAGE_REGEXP,
  replace: (textNode, match) => {
    const [, alt, url] = match;
    const fallbackText = match[0] || alt || "Image";
    if (!createImageAttachment) {
      textNode.setTextContent(fallbackText);
    } else {
      try {
        const attachmentNode = createImageAttachment(url, alt || "Image");
        textNode.replace(attachmentNode);
      } catch {
        textNode.setTextContent(fallbackText);
      }
    }
  },
  trigger: ")",
  type: "text-match",
};

export const TAB: TextMatchTransformer = {
  dependencies: [],
  export: (node) => ($isTabNode(node) ? "\t" : null),
  importRegExp: /\t/,
  regExp: /\t/,
  replace: (textNode) => {
    textNode.replace($createTabNode());
  },
  trigger: "\t",
  type: "text-match",
};

const LINE_BREAK_REGEXP = /<br \/>/;
export const LINE_BREAK: TextMatchTransformer = {
  dependencies: [],
  export: (node) => ($isLineBreakNode(node) ? "<br />" : null),
  importRegExp: LINE_BREAK_REGEXP,
  regExp: LINE_BREAK_REGEXP,
  replace: (textNode) => {
    textNode.replace($createLineBreakNode());
  },
  trigger: ">",
  type: "text-match",
};

const ENTITY_REGEXP = /<entity propertyduid="([^"]+)" value="([^"]+)" \/>/;
const encodeMarkdownAttribute = (value: string) =>
  value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
const decodeMarkdownAttribute = (value: string) =>
  value
    .replaceAll("&quot;", '"')
    .replaceAll("&#x27;", "'")
    .replaceAll("&#39;", "'")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&amp;", "&");
export const ENTITY: TextMatchTransformer = {
  dependencies: [],
  export: (node) => {
    if (node.getType() !== "entity") {
      return null;
    }
    const entity = node as LexicalNode & { getPropertyDuid: () => string; getValue: () => string };
    return `<entity propertyduid="${encodeMarkdownAttribute(entity.getPropertyDuid())}" value="${encodeMarkdownAttribute(entity.getValue())}" />`;
  },
  importRegExp: ENTITY_REGEXP,
  regExp: ENTITY_REGEXP,
  replace: (textNode, match) => {
    if (createEntity) {
      textNode.replace(createEntity(decodeMarkdownAttribute(match[1]), decodeMarkdownAttribute(match[2])));
    }
  },
  trigger: ">",
  type: "text-match",
};

const DART_LINK_REGEXP = /<dartlink link="([^"]+)" kind="([^"]+)"(?: duid="([^"]+)")? label="([^"]+)" \/>/;
export const DART_LINK: TextMatchTransformer = {
  dependencies: [],
  export: (node) => {
    if (node.getType() !== "dart-link") {
      return null;
    }
    const dartLink = node as LexicalNode & {
      getDuid: () => string | undefined;
      getKind: () => string;
      getLabel: () => string;
      getLink: () => string;
    };
    const duid = dartLink.getDuid();
    return `<dartlink link="${encodeMarkdownAttribute(dartLink.getLink())}" kind="${encodeMarkdownAttribute(dartLink.getKind())}"${duid ? ` duid="${encodeMarkdownAttribute(duid)}"` : ""} label="${encodeMarkdownAttribute(dartLink.getLabel())}" />`;
  },
  importRegExp: DART_LINK_REGEXP,
  regExp: DART_LINK_REGEXP,
  replace: (textNode, match) => {
    if (createDartLink) {
      textNode.replace(
        createDartLink(
          decodeMarkdownAttribute(match[1]),
          decodeMarkdownAttribute(match[2]),
          match[3] ? decodeMarkdownAttribute(match[3]) : undefined,
          decodeMarkdownAttribute(match[4])
        )
      );
    }
  },
  trigger: ">",
  type: "text-match",
};

const TABLE_ROW_REGEXP = /^\|(.+)\|\s?$/;
const TABLE_ROW_DIVIDER_REGEXP = /^\|( *:? *-+ *:? *\|)+\s?$/;
export const TABLE: MultilineElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  regExpStart: TABLE_ROW_REGEXP,
  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex, startMatch }) => {
    const tableLines: string[] = [startMatch[0]];
    let lastLineIndex = startLineIndex;
    for (let i = startLineIndex + 1; i < lines.length; i += 1) {
      const line = lines[i];
      if (TABLE_ROW_REGEXP.test(line) || TABLE_ROW_DIVIDER_REGEXP.test(line)) {
        tableLines.push(line);
        lastLineIndex = i;
      } else {
        break;
      }
    }

    const dataLines = tableLines.filter((line) => !TABLE_ROW_DIVIDER_REGEXP.test(line));
    if (dataLines.length === 0) {
      return null;
    }

    const rows = dataLines.map((line) => {
      const match = line.match(TABLE_ROW_REGEXP);
      return (match ? match[1] : line.slice(1, -1)).split("|").map((cell) => cell.trim());
    });

    const numRows = rows.length;
    const numCols = Math.max(...rows.map((row) => row.length));
    const tableNode = $createTableNodeWithDimensions(numRows, numCols, { rows: true, columns: false });

    let rowIndex = 0;
    let rowNode = tableNode.getFirstChild();
    while (rowNode && rowIndex < numRows) {
      if ($isTableRowNode(rowNode)) {
        let colIndex = 0;
        let cellNode = rowNode.getFirstChild();
        while (cellNode && colIndex < numCols) {
          if ($isTableCellNode(cellNode)) {
            const text = rows[rowIndex]?.[colIndex] ?? "";
            if (text) {
              $convertFromMarkdownString(text, cellTransformers, cellNode);
            }
          }
          colIndex += 1;
          cellNode = cellNode.getNextSibling();
        }
      }
      rowIndex += 1;
      rowNode = rowNode.getNextSibling();
    }

    rootNode.append(tableNode);
    return [true, lastLineIndex];
  },
  replace: (rootNode, children, startMatch) => {
    const content = startMatch[0].slice(1, -1).trim();
    const tableNode = $createTableNodeWithDimensions(2, 2, { rows: true, columns: false });
    const firstRow = tableNode.getFirstChild();
    if ($isTableRowNode(firstRow)) {
      const firstCell = firstRow.getFirstChild();
      const secondCell = firstCell?.getNextSibling();
      if (content && $isTableCellNode(firstCell)) {
        $convertFromMarkdownString(content, cellTransformers, firstCell);
      }
      const selectCell = content ? secondCell : firstCell;
      if ($isTableCellNode(selectCell)) {
        const paragraph = selectCell.getFirstChild();
        if ($isElementNode(paragraph)) {
          paragraph.select(0, 0);
        }
      }
    }
    rootNode.append(tableNode);
  },
  export: (node, traverseChildren) => {
    if (!$isTableNode(node)) {
      return null;
    }
    const rows: string[][] = [];
    let rowNode = node.getFirstChild();
    while (rowNode) {
      if ($isTableRowNode(rowNode)) {
        const cells: string[] = [];
        let cellNode = rowNode.getFirstChild();
        while (cellNode) {
          if ($isTableCellNode(cellNode)) {
            cells.push(traverseChildren(cellNode).replace(/\n/g, " ").trim());
          }
          cellNode = cellNode.getNextSibling();
        }
        rows.push(cells);
      }
      rowNode = rowNode.getNextSibling();
    }
    if (rows.length === 0) {
      return null;
    }
    const numCols = Math.max(...rows.map((row) => row.length));
    const lines: string[] = [];
    rows.forEach((row, i) => {
      const paddedRow = [...row, ...Array(numCols - row.length).fill("")];
      lines.push(`| ${paddedRow.join(" | ")} |`);
      if (i === 0) {
        lines.push(`| ${paddedRow.map(() => "---").join(" | ")} |`);
      }
    });
    return lines.join("\n");
  },
  type: "multiline-element",
};
